/**
 * Human Loss Report Handler
 * Handles: Primary Report (PDF/Excel) and Detailed Report (PDF/Excel)
 * ✅ FIXED: Extracts data from human_loss_report array
 * ✅ NEW: Added footer with Letter No., Date, Copy, and Signature blocks
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

/**
 * Load logo image
 */
const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

/**
 * Format date to DD-MM-YYYY
 */
const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}-${month}-${year}`;
  }

  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

/**
 * Format time to HH:MM
 */
const formatTime = (timeStr) => {
  if (!timeStr) return 'NA';
  if (timeStr.includes(':')) {
    const parts = timeStr.split(':');
    return `${parts[0]}:${parts[1]}`;
  }
  return timeStr;
};

/**
 * Generate IST filename
 */
const generateISTFilename = (prefix) => {
  const now = new Date();
  const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime
    .toISOString()
    .split('T')[1]
    .split('.')[0]
    .replace(/:/g, '');
  return `${prefix}_${istDate}_${istTimeStr}`;
};

/**
 * Safe value handler
 */
const getSafeValue = (value) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return 'NA';
  }
  return String(value).trim();
};

/**
 * ✅ NEW: Extract human_loss_report array from various response structures
 */
const extractHumanLossData = (rawData) => {
  console.log('🔍 Extracting human loss data from:', rawData);

  // Direct array
  if (Array.isArray(rawData)) {
    console.log('✅ Found direct array with', rawData.length, 'records');
    return rawData;
  }

  // Backend structure: data.human_loss_report
  if (rawData?.human_loss_report && Array.isArray(rawData.human_loss_report)) {
    console.log(
      '✅ Found human_loss_report array with',
      rawData.human_loss_report.length,
      'records'
    );
    return rawData.human_loss_report;
  }

  // Nested structure: data.data.human_loss_report
  if (
    rawData?.data?.human_loss_report &&
    Array.isArray(rawData.data.human_loss_report)
  ) {
    console.log(
      '✅ Found data.human_loss_report array with',
      rawData.data.human_loss_report.length,
      'records'
    );
    return rawData.data.human_loss_report;
  }

  // Fallback: data.data as array
  if (rawData?.data && Array.isArray(rawData.data)) {
    console.log('✅ Found data array with', rawData.data.length, 'records');
    return rawData.data;
  }

  console.warn('⚠️ No data found in any expected structure');
  return [];
};

/**
 * Handle Human Loss PRIMARY Report PDF
 * Format: Landscape, 10 columns
 * ✅ FIXED: Now extracts from human_loss_report array
 * ✅ NEW: Added footer with Letter No., Date, Copy, and Signature
 */
export const handleHumanLossPrimaryReportPDF = async (
  message,
  format,
  rawData
) => {
  console.log('📋 Human Loss PRIMARY Report PDF generation started');
  if (format !== 'pdf') {
    alert('Human Loss Primary Report only supports PDF format');
    return;
  }

  try {
    const doc = new jsPDF('l', 'pt', 'a4'); // Landscape for 10 columns
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;

    // Load logo
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
    } catch (e) {
      console.warn('⚠️ Logo load failed:', e.message);
    }

    // Add logo
    if (logoImg) {
      const logoWidth = 60;
      const logoHeight = 60;
      const logoX = centerX - logoWidth / 2;
      doc.addImage(logoImg, 'JPEG', logoX, 20, logoWidth, logoHeight);
    }

    // Header
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });

    // ✅ FIXED: Extract date and format title like Web UI
    const reportDate =
      message?.debug?.tool_result?.parameters?.reported_on ||
      message?.debug?.parameters?.reported_on ||
      formatDateToDDMMYYYY(new Date().toISOString().split('T')[0]);
    doc.text(
      `Human Loss - Primary incident Report (Dated on ${reportDate})`,
      centerX,
      135,
      {
        align: 'center',
      }
    );

    // ✅ FIXED: Extract rows using new helper function
    const rows = extractHumanLossData(rawData);
    console.log(`📊 Processing ${rows.length} primary records`);

    if (rows.length === 0) {
      alert('No data available to generate PDF');
      return;
    }

    // ✅ NEW: district name for footer
    const districtName =
      message?.debug?.tool_result?.parameters?.district_name ||
      message?.debug?.parameters?.district_name ||
      rows?.[0]?.distName ||
      rows?.[0]?.district_name ||
      'DISTRICT';

    // Table headers (10 columns)
    const tableHeaders = [
      'Date',
      'Incident Time',
      'Name of district',
      'Block',
      'Name of Disaster',
      'Incident ID',
      'Latitude',
      'Longitude',
      'Number of deaths',
      'Number of injured',
    ];

    const tableData = rows.map((row) => [
      formatDateToDDMMYYYY(
        row.reported_on || row.incidentDate || row.date
      ),
      formatTime(row.incidentTime),
      getSafeValue(row.distName || row.district_name),
      getSafeValue(row.blockName || row.block_name),
      getSafeValue(row.disaster_name || row.disasterName),
      getSafeValue(row.incidentId),
      getSafeValue(row.latitude),
      getSafeValue(row.longitude),
      getSafeValue(
        row.noOfCasualtyDeaths ||
          row.numberOfDeaths ||
          row.number_of_deaths ||
          0
      ),
      getSafeValue(
        row.noOfCasualtyInjured ||
          row.numberOfInjured ||
          row.number_of_injured ||
          0
      ),
    ]);

    // Generate table
    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 160,
      theme: 'grid',
      bodyStyles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        fontStyle: 'normal',
        font: 'Helvetica',
        fontSize: 7,
        halign: 'center',
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'normal',
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
        halign: 'center',
        font: 'Helvetica',
        fontSize: 8,
        cellPadding: 4,
      },
      styles: {
        font: 'Helvetica',
        fontSize: 7,
        cellPadding: 3,
        halign: 'center',
        fontStyle: 'normal',
        overflow: 'linebreak',
      },
      margin: { top: 160, left: 20, right: 20 },
    });

    // ✅ Web UI style footer
    const footerY = pageHeight - 80;
// ✅ FIXED: Right side: Signature + district (ALL at X=520)
doc.setFontSize(10);
doc.setFont('Helvetica', 'normal');

 // Left side: Letter / Date / Copy (no underscores, same as Web UI)
    doc.text('Letter No.: ', 50, footerY);
    doc.text('Date: ', 50, footerY + 20);
    doc.text('Copy: ', 50, footerY + 40);

// Right side: Signature + district (EXACT Web UI text, ALL X=520)
doc.text('Signature of District Officers/ ', 520, footerY);
doc.text('Additional District Magistrate(ADM)(Disaster Management)/', 520, footerY + 20);
doc.text('Officer in Charge(District Disaster Management)/', 520, footerY + 40);
doc.setFont('Helvetica', 'bold');
doc.text(`[${districtName.toUpperCase()} DISTRICT]`, 520, footerY + 60);


    const finalFilename =
      generateISTFilename('Human_loss_primary_report') + '.pdf';
    doc.save(finalFilename);

    console.log('✅ Human Loss Primary PDF downloaded successfully!');
  } catch (error) {
    console.error(
      '❌ Human Loss Primary PDF generation failed:',
      error
    );
    alert(`PDF Generation Error: ${error.message}`);
  }
};

/**
 * Handle Human Loss DETAILED Report PDF
 * ✅ FIXED: Two-table format matching Web UI exactly
 * Format: Primary info table + Casualty details table per incident
 * ✅ NEW: Added footer with Letter No., Date, Copy, and Signature
 */
export const handleHumanLossDetailedReportPDF = async (
  message,
  format,
  rawData
) => {
  console.log('📋 Human Loss DETAILED Report PDF generation started');
  if (format !== 'pdf') {
    alert('Human Loss Detailed Report only supports PDF format');
    return;
  }

  try {
    const doc = new jsPDF('l', 'pt', 'a4'); // Landscape
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;
    let currentY = 160; // Starting Y position

    // Load logo
    let logoImg = null;
    try {
      logoImg = await loadLogo(govtLogo);
    } catch (e) {
      console.warn('⚠️ Logo load failed');
    }

    if (logoImg) {
      const logoWidth = 60;
      const logoHeight = 60;
      const logoX = centerX - logoWidth / 2;
      doc.addImage(logoImg, 'JPEG', logoX, 20, logoWidth, logoHeight);
    }

    // Header
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });

    // NEW: match Web UI header format
    const detailedDateRaw =
      message?.debug?.tool_result?.parameters?.reported_on ||
      message?.debug?.parameters?.reported_on ||
      message?.debug?.tool_result?.parameters?.date ||
      message?.debug?.parameters?.date;
    const detailedDate = formatDateToDDMMYYYY(
      detailedDateRaw || new Date().toISOString().split('T')[0]
    );
    doc.text(
      `Human Loss - Detailed Report (Dated on ${detailedDate})`,
      centerX,
      135,
      {
        align: 'center',
      }
    );

    // Extract rows
    const rows = extractHumanLossData(rawData);
    console.log(`📊 Extracted ${rows.length} incident records`);

    if (rows.length === 0) {
      alert('No data available to generate PDF');
      return;
    }

    // ✅ NEW: district name for footer
    const districtName =
      message?.debug?.tool_result?.parameters?.district_name ||
      message?.debug?.parameters?.district_name ||
      rows?.[0]?.distName ||
      rows?.[0]?.district_name ||
      'DISTRICT';

    // ✅ NEW FORMAT: Loop through each incident and create two tables
    rows.forEach((incident, index) => {
      const serialNo = index + 1;

      // Check if we need a new page
      if (currentY > pageHeight - 150) {
        doc.addPage();
        currentY = 40;
      }

      // ===== TABLE 1: PRIMARY INCIDENT INFO (10 columns) =====
      const primaryHeaders = [
        'S.No.',
        'Date',
        'Incident Time',
        'Name of district',
        'Block',
        'Name of disaster',
        'Incident ID',
        'Latitude',
        'Longitude',
        'No. of deaths',
        'No. of injured',
      ];

      const primaryData = [
        [
          serialNo,
          formatDateToDDMMYYYY(
            incident.reported_on || incident.incidentDate || incident.date
          ),
          formatTime(incident.incidentTime || incident.incident_time),
          getSafeValue(incident.distName || incident.district_name),
          getSafeValue(incident.blockName || incident.block_name),
          getSafeValue(
            incident.disaster_name ||
              incident.disasterName ||
              incident.disaster_type
          ),
          getSafeValue(incident.incidentId || incident.incident_id),
          getSafeValue(incident.latitude),
          getSafeValue(incident.longitude),
          getSafeValue(
            incident.noOfCasualtyDeaths ||
              incident.numberOfDeaths ||
              incident.number_of_deaths ||
              0
          ),
          getSafeValue(
            incident.noOfCasualtyInjured ||
              incident.numberOfInjured ||
              incident.number_of_injured ||
              0
          ),
        ],
      ];

      autoTable(doc, {
        head: [primaryHeaders],
        body: primaryData,
        startY: currentY,
        theme: 'grid',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          halign: 'center',
          font: 'Helvetica',
          fontSize: 7,
          cellPadding: 3,
        },
        bodyStyles: {
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fontStyle: 'normal',
          font: 'Helvetica',
          fontSize: 7,
          halign: 'center',
          cellPadding: 3,
        },
        margin: { left: 20, right: 20 },
      });

      currentY = doc.lastAutoTable.finalY + 5;

      // ===== TABLE 2: CASUALTY DETAILS (18 columns) =====
      const casualtyHeaders = [
        'Type of casualty',
        'Name of deceased',
        "Father's name/husband's name",
        'Age',
        'Gender',
        'Residential address',
        "Victim's activity",
        'Post mortem done',
        'Occupation',
        'Whether the death due to heatstroke has been certified by the competent authority/doctor',
        'Hospitalized',
        'Hospital name',
        'Reported to police station',
        'Police station name',
        'Remarks',
        'Entry date',
        'Payment status',
        'Payment date',
      ];

      // Get casualties from different possible field names
      const casualtyDataVariants = [
        incident.casualtyData,
        incident.casualty_data,
        incident.casualties,
        incident.human_loss_details,
        incident.humanLossDetailsList,
        incident.details,
        incident.detail_data,
      ];

      const casualtiesArray = casualtyDataVariants.find(
        (arr) => Array.isArray(arr) && arr.length > 0
      );

      let casualtyData = [];

      if (casualtiesArray && casualtiesArray.length > 0) {
        // Map each casualty to a row
        casualtyData = casualtiesArray.map((casualty) => [
          getSafeValue(
            casualty.typeOfCasualty ||
              casualty.type_of_casualty ||
              casualty.casualty_type
          ),
          getSafeValue(
            casualty.personName || casualty.person_name || casualty.name
          ),
          getSafeValue(
            casualty.personFamilyMemberName ||
              casualty.person_family_member_name ||
              casualty.father_husband_name
          ),
          getSafeValue(casualty.age),
          getSafeValue(casualty.gender),
          getSafeValue(
            casualty.personAddress ||
              casualty.person_address ||
              casualty.address
          ),
          getSafeValue(
            casualty.activityDuringAccident ||
              casualty.activity_during_accident ||
              casualty.activity
          ),
          casualty.isPostMortem ||
          casualty.is_post_mortem ||
          casualty.post_mortem
            ? 'Yes'
            : 'No',
          getSafeValue(casualty.occupation),
          casualty.heatStrokeCertified ||
          casualty.heat_stroke_certified ||
          casualty.certified_by_doctor
            ? 'Yes'
            : 'No',
          casualty.isHospitalized ||
          casualty.is_hospitalized ||
          casualty.hospitalized
            ? 'Yes'
            : 'No',
          getSafeValue(casualty.hospitalName || casualty.hospital_name),
          casualty.isReportedToPS ||
          casualty.is_reported_to_ps ||
          casualty.reported_to_police
            ? 'Yes'
            : 'No',
          getSafeValue(
            casualty.policeStationName || casualty.police_station_name
          ),
          getSafeValue(casualty.remarks || casualty.remark),
          formatDateToDDMMYYYY(
            casualty.entryDate ||
              casualty.entry_date ||
              incident.reported_on
          ),
          getSafeValue(casualty.paymentStatus || casualty.payment_status),
          formatDateToDDMMYYYY(
            casualty.paymentDate || casualty.payment_date
          ),
        ]);
      } else {
        // No casualties - show empty row with NA
        casualtyData = [
          [
            'NA',
            'NA',
            'NA',
            'NA',
            'NA',
            'NA',
            'NA',
            'No',
            'NA',
            'NA',
            'No',
            'NA',
            'No',
            'NA',
            'NA',
            formatDateToDDMMYYYY(incident.reported_on),
            'NA',
            'NA',
          ],
        ];
      }

      autoTable(doc, {
        head: [casualtyHeaders],
        body: casualtyData,
        startY: currentY,
        theme: 'grid',
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          halign: 'center',
          font: 'Helvetica',
          fontSize: 5.5,
          cellPadding: 2,
        },
        bodyStyles: {
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
          fontStyle: 'normal',
          font: 'Helvetica',
          fontSize: 5.5,
          halign: 'center',
          cellPadding: 2,
        },
        margin: { left: 20, right: 20 },
      });

      currentY = doc.lastAutoTable.finalY + 15; // Space between incidents
    });

    // ✅ Web UI style footer
    const footerY = pageHeight - 80;
    doc.setFontSize(10);
    doc.setFont('Helvetica', 'normal');

    // Left side: Letter / Date / Copy (no underscores, same as Web UI)
    doc.text('Letter No.: ', 50, footerY);
    doc.text('Date: ', 50, footerY + 20);
    doc.text('Copy: ', 50, footerY + 40);

// Right side: Signature + district (EXACT Web UI text, ALL X=520)
doc.text('Signature of District Officers/ ', 520, footerY);
doc.text('Additional District Magistrate(ADM)(Disaster Management)/', 520, footerY + 20);
doc.text('Officer in Charge(District Disaster Management)/', 520, footerY + 40);
doc.setFont('Helvetica', 'bold');
doc.text(`[${districtName.toUpperCase()} DISTRICT]`, 520, footerY + 60);

    const finalFilename =
      generateISTFilename('Human_loss_detailed_report') + '.pdf';
    doc.save(finalFilename);

    console.log('✅ Human Loss Detailed PDF downloaded successfully!');
  } catch (error) {
    console.error(
      '❌ Human Loss Detailed PDF generation failed:',
      error
    );
    alert(`PDF Generation Error: ${error.message}`);
  }
};

/**
 * Check if tool name is human loss primary report
 */
export const isHumanLossPrimaryReport = (toolName) => {
  const reports = [
    'human_loss_primary_report',
    'humanlossprimaryreport',
    'primary_human_loss',
  ];
  return reports.includes(toolName?.toLowerCase());
};

/**
 * Check if tool name is human loss detailed report
 */
export const isHumanLossDetailedReport = (toolName) => {
  const reports = [
    'human_loss_detailed_report',
    'humanlossdetailedreport',
    'detailed_human_loss',
  ];
  return reports.includes(toolName?.toLowerCase());
};

/**
 * Check if tool name is any human loss report
 */
export const isHumanLossReport = (toolName) => {
  return (
    isHumanLossPrimaryReport(toolName) ||
    isHumanLossDetailedReport(toolName)
  );
};
